/**
 * App-side prefixed ids, carried forward from All Channels v1. A prefixed id is
 * self-describing in a log line, an event payload and a support ticket; a bare
 * uuid is not. Cheap to keep, impossible to retrofit once ids are in the wild.
 */
export type EntityKind =
  | "client"
  | "contact"
  | "thread"
  | "message"
  | "event"
  | "task"
  | "memory"
  | "artifact"
  | "artifact_approval"
  | "outcome"
  | "draft"
  | "kb"
  | "spend"
  | "alert"
  | "campaign_send"
  | "appointment"
  | "telemetry"
  // Decision 4 of the 10-02b list: a campaign template approval or revocation (migration 0037).
  | "campaign_approval"
  // U1 RESEARCH (migration 0040): one version of a lead card.
  | "lead_card"
  // V3 SCHEDULER (migration 0045): one set of proposed slots for a consult intent.
  | "consult_proposal"
  // X1 AUTH (migration 0051): a person who signs in, and one signed-in session.
  | "user"
  | "session"
  // X4 TEAM (migration 0054): one open-or-ended hand-over of a thread to a person, and a person-to-person note.
  | "thread_assignment"
  | "handoff_note"
  // Y2 ONBOARD (migration 0056): one onboarding run.
  | "onboard_run"
  // B2 REVENUE (migration 0060): one owner-stated baseline version.
  | "shop_baseline"
  // B5 REVENUE (migration 0061): one experiment registration version, its start approval, its stop.
  | "exp_reg"
  | "exp_start"
  | "exp_stop"
  // B3 CONTACT CAPTURE (migration 0062): one sealed phone or email captured from a customer message.
  | "contact_identity"
  | "competitor_mention"
  // TA-1371 contacts C3 (migration 0067): one owner reminder on a thread.
  | "thread_reminder";

export const ID_PREFIX: Record<EntityKind, string> = {
  client: "cl_",
  contact: "ct_",
  thread: "cv_",
  message: "msg_",
  event: "ev_",
  task: "tsk_",
  memory: "mem_",
  artifact: "art_",
  // The T2 signature on one artifact version (TA-200, migration 0021). Its own
  // prefix rather than sharing `art_`, because in a log line the difference
  // between the artifact and the act of a human signing it is the whole point.
  artifact_approval: "aap_",
  outcome: "out_",
  draft: "df_",
  kb: "kb_",
  spend: "sp_",
  alert: "al_",
  // `campaigns` itself has no app-generated id: it is human-authored (an
  // owner or, later, an authoring surface writes the row), the same reason
  // `budget_windows`/`send_suppressions`/`thread_freezes` never got a kind
  // here either. A `campaign_sends` row IS agent-generated, one per contact
  // per wave, so it earns its own prefix (campaignSweep.ts).
  campaign_send: "cms_",
  // Unlike `campaigns`, an `appointments` row IS created by a coded function
  // (`createAppointment`, packages/db/src/appointments.ts), even though a
  // human is the one who calls it with a real date and time they already
  // know is free (Agent Brains section 5.4: the calendar-truth blocker stays
  // unsolved, but the row it produces is not hand-written SQL). A
  // code-generated id earns its own prefix the same way `outcome` does for
  // `recordOutcome`, another human-triggered but code-executed write.
  appointment: "apt_",
  // One `telemetry_calls` row per gateway call (TA-197). It earns a prefix for
  // the reason above and for one more: the id is minted in `record()`, on the
  // hot path, BEFORE the row is written, so a retried batch can be refused by
  // the primary key rather than landing twice. A bare uuid would do that job
  // too and would say nothing in an operator's grep.
  telemetry: "tel_",
  campaign_approval: "cta_",
  lead_card: "lc_",
  consult_proposal: "cp_",
  user: "usr_",
  session: "ses_",
  thread_assignment: "ta_",
  handoff_note: "hn_",
  onboard_run: "ob_",
  shop_baseline: "sbl_",
  exp_reg: "xrg_",
  exp_start: "xst_",
  exp_stop: "xsp_",
  contact_identity: "idn_",
  // TA-1336 COMPETITOR MENTIONS (0065): one row per customer DM naming another shop or site.
  competitor_mention: "cmn_",
  // TA-1371 contacts C3 (0067): an owner's reminder on a thread; a time and who set it, never text.
  thread_reminder: "trm_",
};

const ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyz";
const SUFFIX_LEN = 16;

/**
 * The rng is injected so fixtures and golden artifacts are reproducible. A
 * pipeline whose output changes on every run cannot be diffed, and an artifact
 * that cannot be diffed cannot be reviewed before it ships (B5 T2 approval).
 */
export function makeIdFactory(rng: () => number = Math.random) {
  let counter = 0;
  return function makeId(kind: EntityKind): string {
    counter += 1;
    let suffix = "";
    for (let i = 0; i < SUFFIX_LEN; i++) {
      const pick = Math.floor(rng() * ALPHABET.length) % ALPHABET.length;
      suffix += ALPHABET[pick] ?? "0";
    }
    // Monotonic counter guarantees uniqueness even if the rng repeats a draw,
    // which Math.random will do inside 500 samples more often than intuition says.
    return `${ID_PREFIX[kind]}${suffix}${counter.toString(36)}`;
  };
}

/**
 * The shape of an id `makeId("memory")` writes: `mem_`, SUFFIX_LEN characters of
 * ALPHABET, then the counter in base 36 (at most eight characters, a counter no
 * process reaches). Room for no sentence: a string of this shape cannot carry a
 * name or a word of the customer's, which is why the held-fact mail and the
 * `pnpm drafts` line accept an id only if it is this shape and not a looser one.
 */
export const MEMORY_ID_SHAPE = new RegExp(`^${ID_PREFIX.memory}[0-9a-z]{${SUFFIX_LEN + 1},${SUFFIX_LEN + 8}}$`);

export function isMemoryId(id: unknown): id is string {
  return typeof id === "string" && MEMORY_ID_SHAPE.test(id);
}
